import { http, httpGet } from "./http";

export type ExploreLayerLegend = {
  value_type: "absolute" | "anomaly" | "categorical";
  palette_type: "sequential" | "diverging" | "categorical";
  palette_colors?: string[] | null;
  units: string | null;
  min_value: number | null;
  max_value: number | null;
  mid_value: number | null;
  labels: {
    low: string;
    mid: string;
    high: string;
  };
  ticks: number[] | null;
  categories: Array<{ value: string; label: string }> | null;
  interpretation_note: string;
  experimental: boolean;
  source_legend_url: string | null;
};

export type ExploreLayerDef = {
  layer_id: string;
  title: string;
  category: string;
  source_type: "external_wms" | "external_tiles" | "internal_s3_tiles";
  time_support: "none" | "daily" | "hourly";
  tile_format: "png" | "pbf";
  minzoom: number;
  maxzoom: number;
  attribution?: string | null;
  disclaimer?: string | null;
  legend?: ExploreLayerLegend | null;
  source?: Record<string, string> | null;
  available: boolean;
  requires_tier?: string | null;
  status?: string;
  vector_available?: boolean;
  vector_time_support?: "none" | "daily" | "hourly" | null;
};

export type LayerVectorFieldFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    layer_id: string;
    u: number;
    v: number;
    speed: number;
    bearing_degrees: number;
    sample_time?: string | null;
  };
};

export type LayerVectorFieldResponse = {
  type: "FeatureCollection";
  features: LayerVectorFieldFeature[];
  meta?: {
    layer_id?: string;
    requested_time?: string;
    resolved_time?: string;
    time_support?: "daily" | "hourly" | null;
    stride?: number;
    sample_count?: number;
    time_fallback_latest?: boolean;
  };
};

export async function fetchLayerCatalog(): Promise<ExploreLayerDef[]> {
  const res = await http.get("/tellus/layers");
  const layers = Array.isArray(res?.data?.layers) ? res.data.layers : [];
  return layers as ExploreLayerDef[];
}

export async function fetchLayerVectorField(opts: {
  layerId: string;
  bbox: [number, number, number, number];
  time?: string | null;
  maxPoints?: number;
  signal?: AbortSignal;
}): Promise<LayerVectorFieldResponse> {
  const bbox = opts.bbox.join(",");
  const params = new URLSearchParams({ bbox });
  if (opts.time) params.set("time", opts.time);
  if (opts.maxPoints != null) params.set("max_points", String(opts.maxPoints));
  const out = await httpGet(`/tellus/layers/${encodeURIComponent(opts.layerId)}/vectors?${params.toString()}`, {
    signal: opts.signal,
  });
  return out as LayerVectorFieldResponse;
}
